import * as React from "react";
import { makeStyles, Button as FluentButton } from "@fluentui/react-components";

export const useTemplateTabViewStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    fontFamily: "Arial, sans-serif",
    fontSize: "12px",
    color: "#6D6E71",
  },
  pageTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#001C71",
    letterSpacing: "0.04em",
    marginBottom: "2px",
  },
  pageSubtitle: {
    fontSize: "11px",
    color: "#6D6E71",
    marginBottom: "14px",
  },
  section: {
    marginBottom: "12px",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "6px",
    overflow: "hidden",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#F5F9F9",
    borderBottom: "0.25pt solid #C4E1E0",
    gap: "8px",
  },
  sectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    backgroundColor: "#001C71",
    color: "#fff",
    fontSize: "10px",
    fontWeight: "700",
    flexShrink: 0,
  },
  sectionTitleText: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#001C71",
    flex: 1,
  },
  sectionBody: {
    padding: "10px 12px",
  },
  tableWrapper: {
    overflowX: "auto",
    marginBottom: "2px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "11px",
  },
  tableHead: {
    backgroundColor: "#F0F6F6",
  },
  th: {
    padding: "5px 6px",
    textAlign: "left" as const,
    fontWeight: "600",
    color: "#001C71",
    borderBottom: "0.25pt solid #C4E1E0",
    whiteSpace: "nowrap" as const,
  },
  td: {
    padding: "4px 6px",
    verticalAlign: "top" as const,
    borderBottom: "0.25pt solid #EEF4F4",
    color: "#6D6E71",
  },
  dragHandle: {
    color: "#BBBBBB",
    cursor: "grab",
    userSelect: "none" as const,
    fontSize: "13px",
    lineHeight: 1,
  },
  paramSubtext: {
    display: "block",
    marginTop: "3px",
    fontSize: "10px",
    color: "#999",
    fontStyle: "italic",
  },
  inlineMetaText: {
    fontSize: "10px",
    color: "#8A8A8A",
    marginTop: "3px",
    lineHeight: 1.25,
  },
  inlineSelect: {
    fontSize: "11px",
    padding: "2px 4px",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "4px",
    color: "#333",
    backgroundColor: "#fff",
    width: "100%",
  },
  inlineCheckbox: {
    width: "14px",
    height: "14px",
    cursor: "pointer",
    accentColor: "#001C71",
  },
  actionCell: {
    display: "flex",
    gap: "2px",
    alignItems: "center",
  },
  emptyRow: {
    padding: "16px 12px",
    textAlign: "center" as const,
    color: "#AAA",
    fontSize: "11px",
    fontStyle: "italic",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
    marginBottom: "8px",
  },
  fieldLabel: {
    display: "block",
    fontSize: "11px",
    fontWeight: "500",
    color: "#001C71",
    marginBottom: "4px",
  },
  templateField: {
    width: "100%",
    padding: "5px 8px",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#333",
    boxSizing: "border-box" as const,
    fontFamily: "Arial, sans-serif",
    outline: "none",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
    },
    "&:focus": {
      border: "0.25pt solid #001C71",
    },
  },
  actionBar: {
    display: "flex",
    gap: "8px",
    paddingTop: "12px",
    marginTop: "4px",
    borderTop: "0.25pt solid #E0E0E0",
  },
  primaryButton: {
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "14px",
    paddingRight: "14px",
    border: "0.25pt solid #7FBDBA",
    backgroundColor: "#001C71",
    color: "#ffffff",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#001C71",
      color: "#ffffff",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
    },
  },
  successBanner: {
    marginTop: "10px",
    padding: "8px 12px",
    backgroundColor: "#E8F5E9",
    border: "0.25pt solid #4CAF50",
    borderRadius: "6px",
    color: "#2E7D32",
    fontSize: "11px",
  },
});

export type TemplateTabViewStyles = ReturnType<typeof useTemplateTabViewStyles>;

export const SectionHeader = ({ styles, num, title }: { styles: TemplateTabViewStyles; num: number; title: string }) => (
  <div className={styles.sectionHeader}>
    <span className={styles.sectionBadge}>{num}</span>
    <span className={styles.sectionTitleText}>{title}</span>
  </div>
);

export const ActionIconButton = ({
  icon,
  onClick,
  disabled,
  title,
}: {
  icon: JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) => (
  <FluentButton
    appearance="subtle"
    size="small"
    icon={icon}
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{ minWidth: "20px", padding: "2px", height: "22px" }}
  />
);
